import React, { useEffect, useRef, useState } from "react";
import { Drawer, Dropdown, Input, Tooltip } from "antd";
import { DsaPlaylistDto, DsaPlaylistEntryDto, DsaScreenDto } from "../../generated/dashboard-client";
import { DeleteOutlined, DownOutlined, EditOutlined, EyeOutlined, LoadingOutlined, LockOutlined, MoreOutlined, RightOutlined, TagOutlined, UnlockOutlined } from "@ant-design/icons";
import { getRendererThumbPathWithPage, getS3Url, getTimeAgoFromString } from "../../util/utils";
import MediaThumb from "./MediaThumb";
import useFocus from "../../hooks/useFocus";
import DSAApi from "../../api/DSAApi";
import { memberState, mobileState } from "../../state/atoms";
import { useRecoilValue } from "recoil";
import PushToScreenMenu from "./PushToScreenMenu";
import { useOnClickOutside } from "usehooks-ts";
import { DisplayUploadDto } from "../../domain/DisplayUploadDto";
import DsaTagChips from "./DsaTagChips";
import DsaEditTags from "./DsaEditTags";
import { DsaTagItemType } from "../../hooks/useDsaTags";

interface DSAPlaylistCardProps {
  it: DsaPlaylistDto;
  setCreateModal: (visible: boolean) => void;
  setDeleteModal: (visible: boolean) => void;
  setSelected: (selected: DsaPlaylistDto) => void;
  playlists: DsaPlaylistDto[];
  setPlaylists: (playlists: DsaPlaylistDto[]) => void;
  locationId: number;
  setPreviewEntries: (entries: DsaPlaylistEntryDto[]) => void;
  setPreviewModal: (visible: boolean) => void;
  pushPlaylistToScreens: (playlist: DsaPlaylistDto, screens: DsaScreenDto[], callback?: () => void) => void;
  pushLoading: boolean;
  screens: DsaScreenDto[];
  uploads: DisplayUploadDto[];
}

export function getPlaylistImage(p: DsaPlaylistDto, uploads: DisplayUploadDto[]) {
  const firstEntry = p.entries?.find(it => it.sequence! === 0)!;
  if (firstEntry.type === 'UPLOAD') {
    const upload = uploads.find(u => u.id === firstEntry.uploadId)!;
    if (upload) return getS3Url(upload.path!);
  } else {
    return getRendererThumbPathWithPage(firstEntry?.designId!, firstEntry?.page || 1);
  }
  return '';
}

export default function DSAPlaylistCard({ it, setCreateModal, setDeleteModal, setSelected, playlists, setPlaylists, locationId, setPreviewEntries, setPreviewModal, pushPlaylistToScreens, pushLoading, screens, uploads }: DSAPlaylistCardProps) {
  const [isRenaming, setRenaming] = useState(false);
  const [name, setName] = useState("");
  const [renameInputRef, setRenameInputFocus] = useFocus();
  const member = useRecoilValue(memberState);
  const [lockedForUser, setLockedForUser] = useState(false);
  const [dropdownView, setDropdownView] = useState('options');
  const [isMenuVisible, setMenuVisible] = useState(false);
  const menuRef = useRef(null);

  const mobile = useRecoilValue(mobileState);
  const [isMobileMenuVisible, setMobileMenuVisible] = useState(false);

  useEffect(() => {
    setLockedForUser(it.locked ? !member.owner : false);
  }, [member, it]);

  useEffect(() => {
    setName(it.name ?? "");
  }, [it]);

  function hideDropdown() {
    setMenuVisible(false);
    setDropdownView('options');
  }

  const handleClickOutside = (ev: MouseEvent) => {
    hideDropdown();
  }

  useOnClickOutside(menuRef, (ev) => handleClickOutside(ev as MouseEvent));

  function editPlaylist(it: DsaPlaylistDto) {
    setSelected(it);
    setCreateModal(true);
    hideDropdown();
    setMobileMenuVisible(false);
  }

  function deletePlaylist(it: DsaPlaylistDto) {
    setSelected(it);
    setDeleteModal(true);
    hideDropdown();
    setMobileMenuVisible(false);
  }

  function startRenaming() {
    if (lockedForUser) return;
    setRenaming(true);
    (setRenameInputFocus as () => void)();
  }

  function toggleLock(it: DsaPlaylistDto) {
    hideDropdown();
    setMobileMenuVisible(false);
    DSAApi.toggleLockPlaylist(it.id!).then(resp => {
      console.log(`Playlist ${it.name} now ${resp.data.locked ? 'locked' : 'unlocked'}`);
      playlists.find(pl => pl.id === it.id)!.locked = resp.data.locked;
      setPlaylists([...playlists]);
    })
  }

  async function rename() {
    setRenaming(false);
    try {
      await DSAApi.updatePlaylist(it.id!, {
        name: name,
        locationId: locationId,
        entries: it.entries
      });
      playlists.find(pl => pl.id! === it.id!)!.name = name;
      setPlaylists(playlists);
    } catch (e) {
      console.error(e);
    }
  }

  function onPlaylistCardClick(e: React.MouseEvent, p: DsaPlaylistDto) {
    if (lockedForUser) return;
    if (e.target instanceof SVGElement || (e.target as HTMLElement).classList.contains('playlist-edit-trigger')) {
      return;
    }
    editPlaylist(p);
  }

  function openPreview() {
    setPreviewEntries(it.entries!.sort((a, b) => { return a.sequence! - b.sequence! }));
    setPreviewModal(true);
    hideDropdown();
  }

  function onPushToScreensMenuClick(e: any) {
    e.stopPropagation();
    if (screens.length === 0) return;
    if (screens.length === 1) {
      onPushToScreensClick(it, screens);
      hideDropdown();
      setMobileMenuVisible(false);
    } else {
      setDropdownView('push-to-screens');
    }
  }

  function onPushToScreensClick(playlist: DsaPlaylistDto, screens: DsaScreenDto[]) {
    pushPlaylistToScreens(playlist, screens, hideDropdown);
  }

  const menuContent = (<div ref={menuRef}>
    <div className="info">
      {mobile && <h3>{it.name}</h3>}
      Edited {getTimeAgoFromString(it.updatedDate!)}
    </div>
    {dropdownView === 'options' ? <ul>
      {!lockedForUser && <li onClick={() => editPlaylist(it)}><EditOutlined />Edit</li>}
      <li onClick={() => openPreview()}>
        <EyeOutlined /> Preview
      </li>
      {!lockedForUser && <li onClick={() => setDropdownView('edit-tags')}><TagOutlined />Edit Tags</li>}
      {member.owner && <li onClick={() => toggleLock(it)}>
        {it.locked ?
          <><UnlockOutlined />Unlock Playlist</> :
          <><LockOutlined />Lock Playlist</>}
      </li>}
      <li className={screens.length === 0 || pushLoading ? 'disabled' : ''} onClick={(e) => onPushToScreensMenuClick(e)}>
        {pushLoading ? <LoadingOutlined/> : <img src="https://timber.mhmcdn.com/site/marketing/icons/ScreenUpload.svg" alt="push to screen" />}
        Push to Screen{screens.length > 1 ? 's' : ''}
        {screens.length > 1 && <RightOutlined className="right-arrow-icon" />}
      </li>
      {!lockedForUser && <li onClick={() => deletePlaylist(it)}><DeleteOutlined />Delete</li>}
    </ul> :
      dropdownView === 'edit-tags' ?
        <DsaEditTags itemType={DsaTagItemType.Playlist} itemId={it.id!} itemName={it.name!} /> :
      dropdownView === 'push-to-screens' ?
        <PushToScreenMenu
          playlist={it}
          onPlaylistPush={onPushToScreensClick}
          pushLoading={pushLoading}
          setDropdownView={setDropdownView}
          screens={screens}
        /> :
        <></>}
  </div>)

  return <li key={it.id} className="playlist-card">
    {!mobile ?
      <Dropdown
        visible={isMenuVisible}
        getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
        overlayClassName="playlist-edit-menu"
        overlay={menuContent}
      >
        <Tooltip title="More" placement="bottom" getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}>
          <div className="playlist-edit-trigger">
            <MoreOutlined onClick={() => { setMenuVisible(!isMenuVisible) }} />
          </div>
        </Tooltip>
      </Dropdown>
      : <>
        <div className="playlist-edit-trigger">
          <MoreOutlined onClick={() => { setMobileMenuVisible(!isMobileMenuVisible) }} />
        </div>
        <Drawer
          placement="bottom"
          visible={isMobileMenuVisible}
          className="mobile-bottom-menu playlist-edit-menu"
          maskClosable={true}
          closeIcon={<DownOutlined/>}
          onClose={() => setMobileMenuVisible(false)}
        >
          {menuContent}
        </Drawer>
      </>
    }

    <div className="img-cont" onClick={(e) => onPlaylistCardClick(e, it)}>
      <MediaThumb src={getPlaylistImage(it, uploads)} alt={it.name} />
    </div>
    {lockedForUser &&
      <Tooltip title="Locked by owner" placement="bottom" getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}>
        <div className="lock-icon">
          <LockOutlined />
        </div>
      </Tooltip>}
    {!isRenaming && (
      <div
        className={`edit-name ${lockedForUser ? 'naming-locked' : ''}`}
        onClick={startRenaming}
      >
        <p>{name ? name : it.name}</p>
        <EditOutlined />
      </div>
    )}
    {!lockedForUser && <div
      className={`editing-name ${isRenaming ? "show" : ""}`}
    >
      <Input
        ref={renameInputRef}
        value={name}
        onChange={(ev) => setName(ev.target.value)}
        onBlur={rename}
      />
    </div>}
    <DsaTagChips itemType={DsaTagItemType.Playlist} itemId={it.id!} />
  </li>
}
